import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { FieldInput } from "@/components/admin/FieldInput";
import { getSetting, putSetting, formatApiError } from "@/lib/cms";

export const SettingsEditor = ({ settingKey, title, mode = "object", fields = [], itemFields = [], onChanged }) => {
  const [value, setValue] = useState(mode === "object" ? {} : []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getSetting(settingKey)
      .then((v) => setValue(v ?? (mode === "object" ? {} : [])))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [settingKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      await putSetting(settingKey, value);
      toast.success("Saved");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div data-testid={`settings-${settingKey}`}>
      <h2 className="mb-6 font-heading text-2xl font-bold tracking-tight text-foreground">{title}</h2>

      {mode === "object" && (
        <div className="max-w-lg space-y-4">
          {fields.map((f) => (
            <div key={f.key}>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-muted-foreground">{f.label}</label>
              <FieldInput field={f} value={value[f.key]} onChange={(v) => setValue((p) => ({ ...p, [f.key]: v }))} />
            </div>
          ))}
        </div>
      )}

      {mode === "tags" && (
        <div className="max-w-lg">
          <FieldInput field={{ type: "tags" }} value={value} onChange={setValue} />
        </div>
      )}

      {mode === "objectlist" && (
        <div className="max-w-2xl space-y-4">
          {(Array.isArray(value) ? value : []).map((item, idx) => (
            <div key={idx} className="flex items-end gap-3 border border-border bg-card p-4">
              {itemFields.map((f) => (
                <div key={f.key} className="flex-1">
                  <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-muted-foreground">{f.label}</label>
                  <FieldInput
                    field={f}
                    value={item[f.key]}
                    onChange={(v) => setValue((arr) => arr.map((it, i) => (i === idx ? { ...it, [f.key]: v } : it)))}
                  />
                </div>
              ))}
              <button
                onClick={() => setValue((arr) => arr.filter((_, i) => i !== idx))}
                className="rounded-lg border border-border p-2 text-destructive hover:border-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          <button
            onClick={() => setValue((arr) => [...(arr || []), {}])}
            className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-primary"
          >
            <Plus className="h-4 w-4" /> Add row
          </button>
        </div>
      )}

      <button
        onClick={save}
        disabled={saving}
        data-testid={`save-${settingKey}-btn`}
        className="mt-6 flex items-center justify-center gap-2 rounded-lg bg-primary px-6 py-2.5 text-sm font-bold uppercase tracking-wide text-white hover:bg-primary/90 disabled:opacity-60"
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}
      </button>
    </div>
  );
};
